import { useState } from 'react';
import styles from './ActorPoseList.module.css';
import {
  getPoseCaptions,
  getPoseExtractionStateLabel,
  type ActorPose,
  type ActorPoseExtractionState,
} from '../../features/actors/poses';
import { useToast } from '../../hooks/useToast';
import { resolveErrorMessage } from '../../utils/getErrorMessage';

type Props = {
  /** 有効世代のポーズ。undefined = 機能フラグoff (BEがキーを付けない) → 何も表示しない */
  poses: ActorPose[] | undefined;
  /** 最新の要求の世代のstate */
  state: ActorPoseExtractionState | null | undefined;
  /** 最新の要求の世代id (pose_extraction_request_id)。再抽出直後の上書きを外す判定に使う */
  requestId?: string | null;
  /** サムネイルのクリック (呼び出し側のライトボックスで開く) */
  onOpenImage: (src: string, alt: string) => void;
  /**
   * 再抽出の実行関数 (platform adminのみ渡す)。指定した場合のみ「再抽出」ボタンを表示する。
   * 戻り値に `extraction_id` (新しい要求のid) があれば、サーバーがその要求を返すまで「再抽出中」を表示する。
   * 呼び出し側は親で `key={actor.id}` を付け、俳優が変わったら状態の上書きを捨てる。
   */
  onRequestExtraction?: () => Promise<unknown>;
  className?: string;
};

/** 上書きの有効範囲を判定するためのpropsの指紋 (stateとポーズのid列)。要求idが取れないときの予備 */
function propsKey(state: ActorPoseExtractionState | null | undefined, poses: ActorPose[] | undefined): string {
  return `${state ?? ''}|${(poses ?? []).map((p) => p.id).join(',')}`;
}

/** 再抽出APIの戻り値から新しい要求idを取り出す (無ければnull) */
function extractionIdOf(result: unknown): string | null {
  if (result && typeof result === 'object' && 'extraction_id' in result) {
    const id = (result as { extraction_id?: unknown }).extraction_id;
    return typeof id === 'string' && id ? id : null;
  }
  return null;
}

type StateOverride = {
  state: ActorPoseExtractionState;
  /** 投入した要求のid。サーバーの requestId がこれになったら (= 新しい要求の状態が返ってきたら) 上書きを外す */
  untilRequestId: string | null;
  /** 要求idが取れなかったときの予備: 設定時点のpropsの指紋が変わったら外す */
  forKey: string;
};

/**
 * 俳優のポーズ一覧 (キャラクターシートから抽出した背景透過の各図)。
 * docs/features/character-sheet-pose-extraction-plan.md §5。周囲に馴染む控えめな表示にする。
 */
export function ActorPoseList({ poses, state, requestId, onOpenImage, onRequestExtraction, className }: Props) {
  const toast = useToast();
  // 再抽出を投入した直後は一覧の再取得を待たずにqueuedとして表示する。
  // 上書きは「サーバーがまだ古い要求を返している間」だけ有効で、新しい要求id (POSTが返したextraction_id) が
  // 返ってきたらサーバーの状態に従う (failed → 再抽出 → failed でもポーズidが変わらないため、state・ポーズidでは区別できない)。
  // 要求idが取れないときは予備として、設定時点のprops (state・ポーズ) が変わらない間だけ有効にする
  const [stateOverride, setStateOverride] = useState<StateOverride | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);

  if (poses === undefined) return null;

  const currentKey = propsKey(state, poses);
  const overrideActive =
    !!stateOverride &&
    (stateOverride.untilRequestId
      ? (requestId ?? null) !== stateOverride.untilRequestId
      : stateOverride.forKey === currentKey);
  const effectiveState = (overrideActive ? stateOverride!.state : null) ?? state ?? null;
  const visiblePoses = poses.filter((p) => !!p.public_url);
  const captions = getPoseCaptions(visiblePoses);
  const stateLabel = getPoseExtractionStateLabel(effectiveState, visiblePoses.length > 0);

  if (visiblePoses.length === 0 && !stateLabel && !onRequestExtraction) return null;

  const handleRequest = async () => {
    if (!onRequestExtraction || isRequesting) return;
    setIsRequesting(true);
    try {
      const result = await onRequestExtraction();
      setStateOverride({ state: 'queued', untilRequestId: extractionIdOf(result), forKey: currentKey });
      toast.success('再抽出を開始しました');
    } catch (err) {
      toast.error(resolveErrorMessage(err) || 'ポーズの再抽出を開始できませんでした');
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <section className={`${styles.root} ${className ?? ''}`} aria-label="ポーズ">
      <div className={styles.header}>
        <span className={styles.heading}>
          {visiblePoses.length > 0 ? `ポーズ(${visiblePoses.length})` : 'ポーズ'}
        </span>
        {stateLabel && <span className={styles.state}>{stateLabel}</span>}
        {onRequestExtraction && (
          <button
            type="button"
            className={styles.requestButton}
            onClick={handleRequest}
            disabled={isRequesting}
          >
            {isRequesting ? '開始中…' : '再抽出'}
          </button>
        )}
      </div>
      {visiblePoses.length > 0 && (
        <ul className={styles.list}>
          {visiblePoses.map((pose, i) => (
            <li key={pose.id} className={styles.item}>
              <button
                type="button"
                className={styles.thumb}
                onClick={() => onOpenImage(pose.public_url!, captions[i])}
                title={pose.description || captions[i]}
              >
                <img src={pose.public_url!} alt={captions[i]} loading="lazy" />
                {!pose.is_full_body && <span className={styles.badge}>顔</span>}
              </button>
              <span className={styles.caption}>{captions[i]}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
